import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { styleMap } from 'lit/directives/style-map.js'
import { when } from 'lit/directives/when.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { filter, fromEvent, lastValueFrom } from 'rxjs'
import { tap, takeUntil } from 'rxjs/operators'
import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { SPRING_SNAPPY } from '@schmancy/utils/animation'
import { reducedMotion$ } from '@schmancy/directives/reduced-motion'
import { SchmancyExpandRoot } from './expand-root.component.js'

/** Dispatch this event on window to close whichever schmancy-expand is currently open */
export const SCHMANCY_EXPAND_REQUEST_CLOSE = 'schmancy-expand-request-close'

@customElement('schmancy-expand')
export default class SchmancyExpand extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}

			summary::-webkit-details-marker {
				display: none;
			}

			summary {
				list-style: none;
				color: inherit;
			}

			.inline-grid {
				display: grid;
				grid-template-rows: 0fr;
				overflow: hidden;
				transition:
					grid-template-rows 300ms cubic-bezier(0.22, 1.25, 0.36, 1),
					opacity 300ms cubic-bezier(0.22, 1.25, 0.36, 1);
				opacity: 0;
			}

			.inline-grid[data-open] {
				grid-template-rows: 1fr;
				opacity: 1;
			}

			.inline-grid > .inner {
				min-height: 0;
				overflow: hidden;
			}
		`,
	]
	@property() summary = ''

	@property({ type: Boolean, reflect: true }) open = false

	@property({ attribute: 'summary-padding' }) summaryPadding = ''

	@property({ attribute: 'content-padding' }) contentPadding = ''

	@property({ type: Boolean, attribute: 'hide-indicator' }) hideIndicator = false

	@property({ type: Number, attribute: 'indicator-rotate' }) indicatorRotate = 90

	/**
	 * `end` reproduces the placement this component always had (the summary row
	 * is reversed, so the chevron sits at the trailing edge) — the same default,
	 * the same name, and the same values `schmancy-details` carries, so the two
	 * disclosure primitives read as one API rather than two dialects.
	 */
	@property({ attribute: 'indicator-placement', reflect: true })
	indicatorPlacement: 'start' | 'end' = 'end'

	@property({ type: Boolean }) backdrop = true

	@property({ type: Boolean }) inline = false

	private summaryRef = createRef<HTMLElement>()
	private contentSlotRef = createRef<HTMLSlotElement>()
	private root: SchmancyExpandRoot | null = null
	private movedNodes: Element[] = []
	private currentIndicatorAnim: Animation | undefined

	connectedCallback() {
		super.connectedCallback()

		fromEvent<KeyboardEvent>(window, 'keydown')
			.pipe(
				filter(e => e.key === 'Escape'),
				filter(() => this.open),
				tap(() => void this.handleClose()),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		// this.root is the teleported portal panel — not this element's own subtree
		fromEvent<PointerEvent>(document, 'pointerdown')
			.pipe(
				filter(() => this.open),
				filter(e => !!this.root && !e.composedPath().includes(this.root)),
				tap(() => void this.handleClose()),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		if (this.movedNodes.length > 0) {
			this.movedNodes.forEach(n => this.appendChild(n))
			this.movedNodes = []
		}
		if (this.root && this.root.children.length === 0) {
			this.root.remove()
			this.root = null
		}
	}

	private async getOrCreateRoot(): Promise<SchmancyExpandRoot> {
		const theme = await lastValueFrom(this.discover<HTMLElement>('schmancy-theme'))
		const container: HTMLElement = theme ?? document.querySelector('schmancy-theme') ?? document.body
		let root = container.querySelector('schmancy-expand-root') as SchmancyExpandRoot | null
		if (!root) {
			root = new SchmancyExpandRoot()
			container.appendChild(root)
		}
		return root
	}

	/** Close the expand portal, animating back to the summary position. */
	public close(): void {
		void this.handleClose()
	}

	/** Programmatically open the expand portal. */
	public expand(): void {
		if (this.open) return
		void this.expandPortal()
	}

	protected override updated(changed: Map<PropertyKey, unknown>) {
		super.updated(changed)
		if (changed.has('open')) this.markIndicatorState()
		if (changed.has('open') && this.open && !this.inline && !this.root) {
			void this.expandPortal()
		}
	}

	/**
	 * Stamps `data-open` on whatever a consumer slotted into `indicator`, so a
	 * supplied chevron can drive its own active-vs-idle tone from the host —
	 * the same contract `schmancy-details` exposes.
	 */
	private markIndicatorState() {
		const slot = this.shadowRoot?.querySelector('slot[name="indicator"]') as HTMLSlotElement | null
		slot?.assignedElements().forEach(el => el.setAttribute('data-open', String(this.open)))
	}

	private toggle() {
		if (this.inline) {
			this.open = !this.open
			this.animateIndicator(this.open)
		} else if (!this.open) {
			void this.expandPortal()
		}
	}

	private handleSummaryClick(e: MouseEvent) {
		e.preventDefault()
		this.toggle()
	}

	private async expandPortal() {
		if (this.inline) {
			this.open = true
			this.animateIndicator(true)
			return
		}

		const root = await this.getOrCreateRoot()
		this.root = root
		const summary = this.summaryRef.value
		const contentSlot = this.contentSlotRef.value
		if (!summary || !contentSlot) return

		const summaryRect = summary.getBoundingClientRect()
		const nodes = contentSlot.assignedElements({ flatten: true })
		if (nodes.length === 0) return

		root.prepare(summaryRect, this, this.hideIndicator, this.backdrop)

		this.movedNodes = [...nodes]
		this.movedNodes.forEach(n => root.appendChild(n))

		root.triggerOpen()

		this.animateIndicator(true)
		this.open = true
	}

	async handleClose() {
		if (this.inline) {
			this.animateIndicator(false)
			this.open = false
			return
		}

		const root = this.root
		const summary = this.summaryRef.value
		if (!root || !summary) return

		const summaryRect = summary.getBoundingClientRect()
		this.animateIndicator(false)

		await root.triggerClose(summaryRect)

		this.movedNodes.forEach(n => this.appendChild(n))
		this.movedNodes = []
		this.open = false
	}

	private animateIndicator(isOpen: boolean) {
		if (reducedMotion$.value) return

		const indicator = this.shadowRoot?.querySelector('.indicator') as HTMLElement | null
		if (!indicator) return

		this.currentIndicatorAnim?.cancel()
		this.currentIndicatorAnim = indicator.animate(
			[
				{ transform: `rotate(${isOpen ? '0deg' : `${this.indicatorRotate}deg`})` },
				{ transform: `rotate(${isOpen ? `${this.indicatorRotate}deg` : '0deg'})` },
			],
			{
				duration: SPRING_SNAPPY.duration,
				easing: SPRING_SNAPPY.easingFallback,
				fill: 'forwards',
			},
		)
	}

	render() {
		const summaryClasses = this.classMap({
			[this.summaryPadding]: true,
			'select-none relative flex items-center gap-2 rounded-xl transition-all duration-150': true,
			'hover:brightness-[0.92] active:brightness-[0.85] cursor-pointer group': true,
			'flex-row': this.indicatorPlacement === 'start',
			'flex-row-reverse': this.indicatorPlacement === 'end',
		})

		return html`
			<div class="w-full rounded-xl">
				<div
					${ref(this.summaryRef)}
					class=${summaryClasses}
					tabindex="0"
					role="button"
					aria-label=${ifDefined(this.ariaLabel || undefined)}
					@click=${this.handleSummaryClick}
					@keydown=${(e: KeyboardEvent) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault()
							this.toggle()
						}
					}}
				>
					${when(
						!this.hideIndicator,
						() => html`
							<span
								class="indicator flex items-center justify-center w-5 h-5 rounded-full shrink-0 opacity-70 group-hover:opacity-100 will-change-transform"
							>
								<slot name="indicator" @slotchange=${this.markIndicatorState}>
									<svg width="20" height="20" viewBox="0 0 24 24" fill="none" class="w-5 h-5" aria-hidden="true">
										<path
											d="M9 6L15 12L9 18"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</slot>
							</span>
						`,
					)}

					<span class="flex-1 font-medium text-base min-w-0">
						<slot name="summary">${this.summary}</slot>
					</span>

					<slot name="actions"></slot>
				</div>

				${
					this.inline
						? html`
								<div class="inline-grid" ?data-open=${this.open}>
									<div class="inner">
										<slot ${ref(this.contentSlotRef)}></slot>
									</div>
								</div>
							`
						: html`
								<div style=${styleMap(this.open ? {} : { display: 'none' })}>
									<slot ${ref(this.contentSlotRef)}></slot>
								</div>
							`
				}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-expand': SchmancyExpand
	}
}
